import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import { Banner } from "@/components/ui/banner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SearchableCombobox } from "@/components/ui/searchable-combobox";
import { StatsGrid, type StatTile } from "@/components/ui/stats-grid";
import { ApiError } from "@/lib/api-client";
import { useOrgAdminDashboard } from "@/hooks/queries/useOrgAdminDashboard";
import type {
  DashboardClinicianData,
  SeatPoolData,
} from "@/hooks/queries/org-admin-dashboard-types";
import { contractNotice } from "@/routes/org-admin/contract-notice";
import {
  accountStatePill,
  calibrationPill,
  countPendingInvites,
  filterClinicians,
  isRowActionable,
  liveScoringPill,
  orgRoleFilterOptions,
  ROSTER_SCOPE_SENTENCE,
  SEAT_STAYS_CONSUMED_SENTENCE,
  termStartLine,
} from "@/routes/org-admin/dashboard-derivations";
import { EditMemberDialog } from "@/routes/org-admin/EditMemberDialog";
import { renewalBlockNotice } from "@/routes/org-admin/renewal-block-notice";
import {
  SeatRequestBanner,
  useSeatRequest,
} from "@/routes/org-admin/seat-request-cta";

// Story 14.11 — Seats & allocation (Screen 26, FR76/FR77/FR83). The org_admin's
// seat-pool view: four tiles, the roster with its account-state and
// live-scoring flags, client-side filters, and the Edit-member shell. It reads
// the SAME GET /org-admin/dashboard payload the home screen does — no new
// endpoint, no second source of truth for the roster (story D1).
//
// INV-1 / UX D3 hold verbatim: zero PHI, zero numeric calibration values. Every
// clinical fact on this page is a state flag, and the only number beside a
// clinician is the "{N}/{M} cases" progress count.
//
// Three W3 reconciliations vs the mockup, all recorded in the story:
//   * the mockup's "Seat" COLUMN becomes the account-state column (every
//     representable row has a seat, so Assigned/"Not assigned"/"N/A" collapse)
//     and its "Seat" FILTER is dropped for the same reason;
//   * live-scoring labels are Enabled/Blocked (epics literal), not "Locked";
//   * the tiles read Capacity/Consumed/Remaining (the FR83 relabel) rather than
//     the mockup's pre-FR83 "Assigned/Available".
//
// FR83 is load-bearing here: nothing on this screen implies that removing a
// clinician frees a seat, because it does not — the seat stays consumed within
// the current contract term, and the capacity banner says so. Story 21.2
// (FR87) scoped that sentence to the CURRENT term, because a renewal now closes
// one and resets the pool; the term-start line under the StatsGrid says which
// term the tiles above it are counting.

const CAPACITY_BANNER_TITLE = "Seat capacity changes are handled by CITS.";
// The FR83 sentence is composed in from the shared const rather than written
// out here, so this banner and Story 14.6's removal confirmation are the same
// words by construction.
const CAPACITY_BANNER_BODY =
  "Submitting Request additional seats emails the AMPS Director, who arranges seat-count adjustments through CITS's invoicing process. Once capacity is raised, the new seats are immediately invitable and share the existing contract end date. " +
  SEAT_STAYS_CONSUMED_SENTENCE;

const ALL_ROLES = "";

export function SeatsAllocationScreen() {
  const { data, error, isPending, isError, refetch } = useOrgAdminDashboard();
  const seatRequest = useSeatRequest();

  const [search, setSearch] = useState("");
  const [orgRole, setOrgRole] = useState(ALL_ROLES);
  // The ID, never the row OBJECT: 14.11 held the object captured at click time,
  // so a background refetch updated the table beneath an open dialog but not the
  // dialog itself — and 14.4's save posts all three Identity fields at once, so
  // a stale capture would write back the two the admin never touched
  // (seam-1411-edit-member-fields, code-review-plus 2026-08-11).
  const [editingId, setEditingId] = useState<string | null>(null);

  const clinicians = useMemo(() => data?.clinicians ?? [], [data]);
  const roleOptions = useMemo(
    () => orgRoleFilterOptions(clinicians),
    [clinicians],
  );
  // Keyed on the roster, NOT on the filters: the tile counts the whole roster,
  // so a keystroke in Search must not recompute it (the sibling derivations
  // above already hold that line).
  const pendingInvites = useMemo(
    () => countPendingInvites(clinicians),
    [clinicians],
  );
  // Purely client-side (AC7): filtering re-derives from the loaded rows and
  // never touches the query, so no keystroke costs a request.
  const visibleRows = useMemo(
    () => filterClinicians(clinicians, { search, orgRole }),
    [clinicians, search, orgRole],
  );

  // AC9 — null when the organization has no term start, and the caller renders
  // nothing at all rather than a placeholder.
  const termStart = termStartLine(data?.current_term_started_at);

  // Story 21.3 (AC12) — null while the query is pending and null when nothing
  // is blocked, which are the same branch on purpose: a screen must not warn
  // about a commercial state it has not read.
  const renewalBlock = renewalBlockNotice(data?.renewal_block);

  const hasNoProfessionalOrg =
    error instanceof ApiError && error.code === "ORG-002";

  // Story 14.12's shared copy — null while pending/errored, so no affordance is
  // gated on a contract state we have not read.
  const contract = contractNotice(data?.contract_status, data?.contract_end_date);

  return (
    <section className="flex flex-col gap-6">
      <header className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className="flex flex-col gap-1">
          <h1 className="display-xs text-[color:var(--text-primary)]">
            Seat pool &amp; allocation
          </h1>
          <p className="text-sm text-[color:var(--text-tertiary)]">
            Track seat assignments inside this organization. To increase total
            capacity, submit the Request additional seats CTA — CITS adjusts
            capacity directly with you.
          </p>
        </div>
        {/* AC9/AC10 — "Add person" is gated by the contract window (the backend
            would refuse it 403 ORG-010), while "Request additional seats" is the
            named exemption: it stays enabled in every contract state because it
            is the only way out of one. */}
        <div className="flex shrink-0 flex-col items-start gap-1 md:items-end">
          <div className="flex items-center gap-3">
            {contract ? (
              <Button
                variant="secondary"
                disabled
                aria-disabled="true"
                title={contract.title}
                aria-describedby="seats-add-person-blocked-reason"
              >
                Add person
              </Button>
            ) : (
              <Button asChild variant="secondary">
                <Link to="/org-admin/add-person">Add person</Link>
              </Button>
            )}
            <Button
              variant="primary"
              onClick={seatRequest.request}
              disabled={seatRequest.disabled}
            >
              Request additional seats
            </Button>
          </div>
          {contract ? (
            <p
              id="seats-add-person-blocked-reason"
              className="text-xs text-[color:var(--text-tertiary)]"
            >
              {contract.title}
            </p>
          ) : null}
        </div>
      </header>

      {contract ? (
        <Banner variant="warn" title={contract.title}>
          {contract.body}
        </Banner>
      ) : null}

      <SeatRequestBanner control={seatRequest} />

      {hasNoProfessionalOrg ? (
        <Banner variant="info" title="No organization yet">
          Your account isn&apos;t linked to an organization seat pool yet. CITS
          sets up organizations directly — once yours is provisioned, the seat
          pool and roster appear here.
        </Banner>
      ) : isError ? (
        <Banner variant="danger" title="Couldn't load your seat pool">
          <div className="flex flex-col gap-2">
            <span>
              We couldn&apos;t load the seat pool and roster. Please try again.
            </span>
            <span>
              <Button variant="secondary" size="sm" onClick={() => void refetch()}>
                Try again
              </Button>
            </span>
          </div>
        </Banner>
      ) : null}

      {isPending ? (
        <div
          className="flex items-center justify-center rounded-2xl border border-[color:var(--border-primary)] bg-[color:var(--bg-primary)] px-6 py-12 text-sm text-[color:var(--text-tertiary)]"
          role="status"
          aria-live="polite"
          aria-busy="true"
        >
          Loading your seat pool…
        </div>
      ) : data ? (
        <>
          <StatsGrid stats={seatsStats(data.seat_pool, pendingInvites)} />

          {/* AC9. Directly under the tiles it scopes, and ABOVE the CITS
              capacity banner — the stacking agreed with Story 21.3 at the
              2026-09-14 chain-check is: this line → 21.3's renewal-blocked
              `warn` banner → this `info` banner. Same shared constant Screen 25
              renders inside its Seat health card; nothing at all when the
              organization has no term start. */}
          {termStart && (
            <p className="text-xs text-[color:var(--text-tertiary)] [font-variant-numeric:tabular-nums]">
              {termStart}
            </p>
          )}

          {/* Story 21.3 (AC12). The middle of the ruled three-way stack: the
              term-start line above scopes the tiles, this warns that the NEXT
              term is short, and the capacity banner below explains who changes
              seat counts. Renders nothing at all when `renewal_block` is null or
              absent — no wrapper, no spacing — so a healthy organization's
              layout is byte-identical to what 21.2 shipped. */}
          {renewalBlock && (
            <Banner variant="warn" title={renewalBlock.title}>
              {renewalBlock.body}
            </Banner>
          )}

          <Banner variant="info" title={CAPACITY_BANNER_TITLE}>
            {CAPACITY_BANNER_BODY}
          </Banner>

          <div className="flex flex-col gap-4 rounded-2xl border border-[color:var(--border-primary)] bg-[color:var(--bg-primary)] px-5 py-4">
            <div className="flex flex-col gap-1">
              <h2 className="text-sm font-semibold text-[color:var(--text-primary)]">
                Roster
              </h2>
              {/* Shared const, not prose: Screen 25's roster now says the same
                  thing about the same rows, and one sentence in one place is
                  what keeps them saying it identically. */}
              <p className="text-sm text-[color:var(--text-tertiary)]">
                {ROSTER_SCOPE_SENTENCE}
              </p>
            </div>

            <div className="grid gap-3 md:grid-cols-2">
              <div className="flex flex-col gap-1">
                <Label htmlFor="roster-search">Search</Label>
                <Input
                  id="roster-search"
                  type="search"
                  placeholder="Name or email…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <div className="flex flex-col gap-1">
                <Label htmlFor="roster-role">Org role label</Label>
                <SearchableCombobox
                  id="roster-role"
                  options={roleOptions.map((l) => ({ value: l, label: l }))}
                  value={orgRole}
                  onChange={setOrgRole}
                  placeholder="All roles"
                  searchAriaLabel="Filter by org role"
                  showSearch={false}
                />
              </div>
            </div>

            {clinicians.length === 0 ? (
              <Banner variant="info" title="No clinicians on seats yet">
                Clinicians appear here once they are invited to a seat in your
                organization.
              </Banner>
            ) : visibleRows.length === 0 ? (
              <p className="py-6 text-center text-sm text-[color:var(--text-tertiary)]">
                No one matches these filters.
              </p>
            ) : (
              <RosterTable
                rows={visibleRows}
                requiredCaseCount={data.required_case_count}
                onEdit={(row) => setEditingId(row.user_id)}
              />
            )}
          </div>

          {/* The contract state rides down from the payload this screen already
              read (F3). The dialog runs the same gate as the "Add person" CTA
              above — one screen ran two policies, and the member actions were
              the half that let an admin all the way through a destructive
              confirmation before the server refused ORG-010. */}
          <EditMemberDialog
            member={clinicians.find((c) => c.user_id === editingId) ?? null}
            requiredCaseCount={data.required_case_count}
            contractStatus={data.contract_status}
            contractEndDate={data.contract_end_date}
            onOpenChange={(open) => {
              if (!open) setEditingId(null);
            }}
          />
        </>
      ) : null}
    </section>
  );
}

/**
 * This screen's four tiles. The grid itself is the shared
 * `components/ui/stats-grid` primitive (Story 15.7 D5); the ARRAY stays here,
 * because the fourth label is this screen's and carries its own history.
 */
function seatsStats(seatPool: SeatPoolData, pendingInvites: number): StatTile[] {
  return [
    { label: "Capacity", value: seatPool.capacity },
    { label: "Consumed", value: seatPool.consumed },
    { label: "Remaining", value: seatPool.remaining },
    // "Awaiting activation", not "Pending invites" (PDM 2026-08-12): the count
    // is `account_state === 'invited'`, and `invite_clinician` mints
    // `is_active = is_legacy`, so a LEGACY invitee is active — and therefore
    // "Assigned" — the moment they are invited. The old label promised a count
    // of outstanding invitations and delivered a count of unactivated accounts.
    // The derivation was right; the word was wrong.
    { label: "Awaiting activation", value: pendingInvites },
  ];
}

function RosterTable({
  rows,
  requiredCaseCount,
  onEdit,
}: {
  rows: DashboardClinicianData[];
  requiredCaseCount: number;
  onEdit: (row: DashboardClinicianData) => void;
}) {
  return (
    <div className="overflow-x-auto rounded-2xl border border-[color:var(--border-primary)]">
      <table className="w-full border-collapse text-left text-sm">
        <caption className="sr-only">
          Seat roster for your organization
        </caption>
        <thead>
          <tr className="border-b border-[color:var(--bg-quaternary)] text-xs uppercase tracking-wide text-[color:var(--text-tertiary)]">
            <th scope="col" className="px-4 py-3 font-semibold">
              Person
            </th>
            <th scope="col" className="px-4 py-3 font-semibold">
              Org role
            </th>
            <th scope="col" className="px-4 py-3 font-semibold">
              State
            </th>
            <th scope="col" className="px-4 py-3 font-semibold">
              Calibration
            </th>
            <th scope="col" className="px-4 py-3 font-semibold">
              Live scoring
            </th>
            <th scope="col" className="px-4 py-3 font-semibold">
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const name =
              [row.first_name, row.last_name].filter(Boolean).join(" ") || "—";
            // AC8. The term scope is the SECOND argument because it only ever
            // re-labels `removed` — every other state reads identically in both
            // terms, so Screen 25 and Screen 26 share one resolution.
            const state = accountStatePill(row.account_state, row.term_scope);
            // Calibration is the 14.3 pill VERBATIM — account state never leaks
            // into the calibration enum, so an invited clinician reads "Not
            // started" here and "Invited" in the State column.
            const calibration = calibrationPill(
              row.calibration_state,
              row.completed_cases,
              requiredCaseCount,
            );
            const live = liveScoringPill(row.live_scoring);
            return (
              <tr
                key={row.user_id}
                className="border-b border-[color:var(--bg-quaternary)] align-middle last:border-b-0"
              >
                <td className="px-4 py-3">
                  <div className="font-medium text-[color:var(--text-primary)]">
                    {name}
                  </div>
                  <div className="text-xs text-[color:var(--text-tertiary)]">
                    {row.email}
                  </div>
                </td>
                <td className="px-4 py-3 text-[color:var(--text-secondary)]">
                  {row.organization_role_label}
                </td>
                <td className="px-4 py-3">
                  <Badge variant={state.variant}>{state.label}</Badge>
                </td>
                {/* tabular-nums: the pending pill's "{N}/{M} cases" makes this a
                    numeric column, so its digits align down the roster. */}
                <td className="px-4 py-3 [font-variant-numeric:tabular-nums]">
                  <Badge variant={calibration.variant}>
                    {calibration.label}
                  </Badge>
                </td>
                <td className="px-4 py-3">
                  <Badge variant={live.variant}>{live.label}</Badge>
                </td>
                <td className="px-4 py-3">
                  {/* aria-label, not sr-only text: a per-row "Edit" needs a
                      distinguishable accessible name, and appending the name as
                      TEXT would put a second copy of it in the row's DOM.
                      A REMOVED row (Story 14.6, FR83) renders no action at all:
                      the slot is dead, and every member action against it is
                      refused ORG-008 server-side. */}
                  {isRowActionable(row) ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      aria-label={`Edit ${name}`}
                      onClick={() => onEdit(row)}
                    >
                      Edit
                    </Button>
                  ) : (
                    <span className="text-xs text-[color:var(--text-tertiary)]">
                      —
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
